import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, Eye, User, TrendingUp, FileText, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getPublishedNews, getArticleCategories, Article, ContentCategory, getImageUrl, formatDate, formatReadingTime } from '@/lib/api';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useMetadata } from '@/hooks/useMetadata';

const NewsList = () => {
  useMetadata({
    title: 'Artikel & Kabar Terbaru',
    description: 'Temukan informasi terbaru, tips kesehatan mental, dan artikel edukasi dari psikolog profesional MindfulMe.',
    keywords: 'artikel kesehatan mental, tips psikologi, edukasi mental, Gen Z Indonesia',
    canonicalUrl: 'https://mindfulme.pusatandalan.com/news',
  });

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNews = async (page = 1, search = '', category = selectedCategory) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        sort: sortBy as 'latest' | 'popular' | 'oldest' | 'title',
        category: category === 'all' ? undefined : category,
        search: search || undefined,
      };

      const response = await getPublishedNews(params);

      // The getPublishedNews function already returns an array
      if (Array.isArray(response)) {
        setArticles(prev => (page === 1 ? response : [...prev, ...response]));
        setHasMore(!search && response.length === params.limit);
      } else {
        console.error('Unexpected response format:', response);
        setArticles([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setArticles([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchNews(1, term, selectedCategory);
  };

  const loadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchNews(nextPage, searchTerm, selectedCategory);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryData = await getArticleCategories();
        setCategories(categoryData);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    fetchNews(1, searchTerm, selectedCategory);
  }, [sortBy, selectedCategory]);

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section - Premium visual styling with floating blobs */}
      <section className="relative bg-gradient-hero pt-36 pb-20 overflow-hidden text-white">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-10 right-[15%] w-80 h-80 bg-accent/25 rounded-full blur-3xl animate-blob-float-slow"></div>
        <div className="absolute bottom-5 left-[10%] w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-blob-float-medium"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/70 mb-8 font-medium">
            <Link to="/" className="hover:text-white transition-colors">
              Beranda
            </Link>
            <span>/</span>
            <span className="text-secondary-light font-bold">Berita & Artikel</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-none animate-slide-up">
              Berita & Artikel{" "}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-light to-secondary-light">
                Kesehatan Mental
              </span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed font-medium animate-slide-up">
              Dapatkan informasi terbaru seputar kesehatan mental, kesejahteraan,
              dan tips praktis dari tim ahli kami untuk mendukung perjalanan kesehatan mental Anda.
            </p>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-4xl mx-auto">
              {[
                { icon: TrendingUp, val: `${articles.length}+`, label: "Artikel Terbaru", color: "text-secondary-light" },
                { icon: User, val: "10+", label: "Kontributor Ahli", color: "text-accent-light" },
                { icon: Eye, val: "1000+", label: "Pembaca Setia", color: "text-secondary-light" },
                { icon: Calendar, val: "Harian", label: "Update Konten", color: "text-accent-light" }
              ].map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 shadow-lg hover:scale-105 transition-transform duration-300">
                    <Icon className={`h-7 w-7 ${stat.color} mx-auto mb-3`} />
                    <div className="text-2xl font-extrabold text-white">{stat.val}</div>
                    <div className="text-xs text-white/70 font-semibold uppercase tracking-wider mt-1">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="py-12 bg-background relative z-20 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card rounded-2xl p-6 md:p-8 border border-border/40 shadow-xl">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Cari artikel berdasarkan judul, topik, atau penulis..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-12 h-14 text-base border-border/60 focus:border-primary focus:ring-primary/10 rounded-xl"
                  />
                </div>
              </div>

              {/* Sort Select */}
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-full lg:w-[200px] h-14 border-border/60 focus:border-primary rounded-xl font-medium">
                  <SelectValue placeholder="Urutkan" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="latest">📅 Terbaru</SelectItem>
                  <SelectItem value="popular">🔥 Terpopuler</SelectItem>
                  <SelectItem value="oldest">📜 Terlama</SelectItem>
                  <SelectItem value="title">🔤 Abjad</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full lg:w-[220px] h-14 border-border/60 focus:border-primary rounded-xl font-medium">
                  <SelectValue placeholder="Filter kategori" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all">📚 Semua Kategori</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters Display */}
            {(searchTerm || selectedCategory !== 'all') && (
              <div className="mt-5 flex flex-wrap items-center gap-2 pt-4 border-t border-border/20">
                <span className="text-sm text-muted-foreground font-medium">Filter aktif:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="gap-1.5 py-1 px-3 bg-primary/5 text-primary border border-primary/10 rounded-lg text-xs font-semibold">
                    "{searchTerm}"
                    <button
                      onClick={() => handleSearch('')}
                      className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {selectedCategory !== 'all' && (
                  <Badge variant="secondary" className="gap-1.5 py-1 px-3 bg-secondary/5 text-secondary border border-secondary/10 rounded-lg text-xs font-semibold">
                    {categories.find((item) => item.slug === selectedCategory)?.name || selectedCategory}
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className="ml-1 hover:bg-secondary/20 rounded-full p-0.5"
                    >
                      ×
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Articles Grid Section */}
      <section className="py-16 bg-gradient-to-b from-white via-primary/5 to-white relative overflow-hidden">
        <div className="absolute top-10 left-[5%] w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-10 right-[5%] w-72 h-72 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Loading State */}
          {loading && currentPage === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass-card p-5 rounded-2xl border border-border/40 animate-pulse flex flex-col h-[400px]">
                  <div className="aspect-video w-full bg-muted rounded-xl mb-4" />
                  <div className="h-4 bg-muted rounded w-1/4 mb-3" />
                  <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-12 bg-muted rounded w-full mb-6" />
                  <div className="mt-auto flex justify-between items-center">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Articles Grid */}
              {articles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="glass-card rounded-3xl p-12 max-w-2xl mx-auto border border-border/40 shadow-sm">
                    <FileText className="mx-auto h-20 w-20 text-muted-foreground/30 mb-6" />
                    <h3 className="text-2xl font-bold text-foreground mb-4">
                      {searchTerm ? 'Tidak Ada Artikel Ditemukan' : 'Belum Ada Artikel Tersedia'}
                    </h3>
                    <p className="text-muted-foreground text-base mb-8 leading-relaxed">
                      {searchTerm
                        ? 'Coba gunakan kata kunci pencarian yang lain atau lihat artikel-artikel populer kami.'
                        : 'Tim kami sedang mempersiapkan artikel menarik untuk Anda. Silakan kembali lagi nanti.'}
                    </p>
                    {searchTerm && (
                      <Button onClick={() => handleSearch('')} size="lg" className="rounded-xl">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Reset Pencarian
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {articles.map((article) => (
                      <Card key={article.id} className="glass-card hover:glass-card-hover border-border/40 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2.5 transition-all duration-500 group rounded-2xl flex flex-col h-full">
                        {/* Featured Image */}
                        <div className="aspect-video overflow-hidden bg-muted relative">
                          {article.featured_image ? (
                            <img
                              src={getImageUrl(article.featured_image)}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                              <FileText className="h-12 w-12 text-muted-foreground/40" />
                            </div>
                          )}
                          <div className="absolute top-4 left-4">
                            <Badge className="bg-white/95 backdrop-blur-sm text-primary border border-primary/10 text-xs font-bold py-1 px-3 shadow-md hover:bg-white transition-colors">
                              {article.category?.name || 'Artikel'}
                            </Badge>
                          </div>
                        </div>

                        <CardHeader className="pb-3 flex-grow p-6">
                          <div className="flex items-center text-xs text-muted-foreground mb-3">
                            <Calendar className="h-3.5 w-3.5 mr-1.5 text-primary/60" />
                            {formatDate(article.published_at || article.created_at)}
                          </div>
                          <CardTitle className="line-clamp-2 text-lg font-bold group-hover:text-primary transition-colors leading-snug">
                            <Link to={`/news/${article.slug}`}>
                              {article.title}
                            </Link>
                          </CardTitle>
                          <CardDescription className="line-clamp-2 text-sm text-muted-foreground mt-2.5 leading-relaxed">
                            {article.excerpt}
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="pt-0 border-t border-border/10 mt-auto bg-white/30 backdrop-blur-sm p-6">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                            <div className="flex items-center gap-1.5 font-medium text-foreground/80">
                              <User className="h-3.5 w-3.5 text-primary" />
                              <span className="truncate max-w-[120px]">
                                {article.author?.name || 'Anonymous'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-secondary" />
                                <span>{formatReadingTime(article.read_time_minutes)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Eye className="h-3.5 w-3.5 text-accent" />
                                <span>{article.view_count}</span>
                              </div>
                            </div>
                          </div>

                          {/* Tags */}
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {article.tags.slice(0, 2).map((tag) => (
                                <Badge key={tag.id} variant="outline" className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 border-border/60 text-muted-foreground bg-white/40">
                                  #{tag.name}
                                </Badge>
                              ))}
                              {article.tags.length > 2 && (
                                <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 bg-white/40 border-border/60 text-muted-foreground">
                                  +{article.tags.length - 2}
                                </Badge>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Load More Button */}
                  {hasMore && !searchTerm && (
                    <div className="text-center">
                      <Button
                        onClick={loadMore}
                        disabled={loading}
                        size="lg"
                        className="gradient-primary min-w-[180px] h-14 text-base shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 rounded-xl px-8"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Memuat...
                          </>
                        ) : (
                          <>
                            Muat Lebih Banyak
                            <TrendingUp className="ml-2 h-5 w-5 animate-pulse" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsList;